import { PriceAlert } from '../services/priceAlerts.ts/priceAlertsService';
import { PriceAlertCardData } from '../components/dashboard/PriceAlertsCard';
import { Platform } from '../types/Platforms';

/**
 * Transforms raw PriceAlert data to PriceAlertCardData format for the PriceAlertsCard component
 * @param priceAlerts - Array of price alerts from the store
 * @param platformMap - Map of platform IDs to platform objects (to get platform names)
 * @returns Array of PriceAlertCardData formatted for the card component
 */
export function transformPriceAlertsForCard(
  priceAlerts: PriceAlert[],
  platformMap: Map<string, { id: string; platformName: string }>
): PriceAlertCardData[] {
  return priceAlerts.map(alert => {
    const percentChange = ((alert.toPrice - alert.fromPrice) / alert.fromPrice) * 100;
    const platform = platformMap.get(alert.restaurantPlatformId);

    return {
      name: alert.productName,
      platform: (platform?.platformName?.toLowerCase() || 'unknown') as Platform,
      toDate: alert.toDate,
      percentChange: percentChange
    };
  });
}

/**
 * Filters price alerts by date range
 * @param priceAlerts - Array of price alerts
 * @param startDate - Start date of the range
 * @param endDate - End date of the range
 * @returns Filtered array of price alerts
 */
export function filterPriceAlertsByDateRange(
  priceAlerts: PriceAlert[],
  startDate: Date,
  endDate: Date
): PriceAlert[] {
  return priceAlerts.filter(alert => {
    const alertDate = new Date(alert.toDate);
    return alertDate >= startDate && alertDate <= endDate;
  });
}

/**
 * Gets the top N price alerts sorted by absolute percentage change
 * @param priceAlerts - Array of price alerts
 * @param limit - Maximum number of alerts to return
 * @returns Top N price alerts sorted by absolute percentage change (descending)
 */
export function getTopPriceAlerts(
  priceAlerts: PriceAlert[],
  limit: number = 5
): PriceAlert[] {
  return [...priceAlerts]
    .sort((a, b) => {
      const percentChangeA = Math.abs((a.toPrice - a.fromPrice) / a.fromPrice);
      const percentChangeB = Math.abs((b.toPrice - b.fromPrice) / b.fromPrice);
      return percentChangeB - percentChangeA;
    })
    .slice(0, limit);
}
